import {
	Check,
	CheckCheck,
	CircleCheck,
	FolderInput,
	FolderOpen,
	Loader2,
	MailCheck,
	MailWarning,
	Plus,
	Search,
	Tag,
	UserPlus,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ActionSheet, BottomSheet, BottomSheetTextButton } from '@/components/mobile';
import { cn } from '@/lib/utils';
import { useBulkConversationActions } from '../ActionBar';

function BarButton({ icon: Icon, label, onClick, disabled }) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-primary active:opacity-50 disabled:text-muted-foreground disabled:opacity-60"
		>
			<Icon className="size-6" strokeWidth={1.75} aria-hidden="true" />
			<span className="max-w-full truncate">{label}</span>
		</button>
	);
}

/**
 * The phone's Select-mode toolbar, pinned to the bottom in place of the tab
 * bar: Mark, Assign, Move and Tag for the selected conversations. It runs the
 * same requests as the desktop ActionBar.
 */
export function MobileBulkBar({ onFinished }) {
	const bulk = useBulkConversationActions();
	const [sheet, setSheet] = useState(null);
	const count = bulk.selectedConversations.length;
	const disabled = !count || bulk.isLoading;

	const run = (action) => async () => {
		setSheet(null);
		await action();
		onFinished?.();
	};

	// Users without a name show their username (email), as the row menu does.
	const userOptions = (bulk.assigneeOptions[0]?.options || []).map((option) => {
		if (option.label) return option;
		const user = (bulk.assignees || []).find((a) => `USER_${a._id}` === option.value);
		return { ...option, label: user?.username || user?.email || 'Unnamed user' };
	});
	const departmentOptions = bulk.assigneeOptions[1]?.options || [];

	return (
		<>
			{/* Sticks to the bottom of the scrolling phone list, which ends above it. */}
			<div className="sticky bottom-0 z-20 shrink-0 border-t border-border/70 bg-background/90 pb-safe backdrop-blur-xl supports-[backdrop-filter]:bg-background/80 md:hidden">
				{bulk.isLoading ? (
					<div className="flex h-[3.25rem] items-center justify-center gap-2 text-[13px] text-muted-foreground" role="status">
						<Loader2 className="size-4 animate-spin" aria-hidden="true" />
						Updating {count} conversation{count === 1 ? '' : 's'}…
					</div>
				) : (
					<div className="flex h-[3.25rem] items-stretch px-1">
						<BarButton icon={CheckCheck} label="Mark" disabled={disabled} onClick={() => setSheet('mark')} />
						<BarButton icon={UserPlus} label="Assign" disabled={disabled} onClick={() => setSheet('assign')} />
						<BarButton icon={FolderInput} label="Move" disabled={disabled} onClick={() => setSheet('move')} />
						<BarButton icon={Tag} label="Tag" disabled={disabled} onClick={() => setSheet('tag')} />
					</div>
				)}
			</div>

			<ActionSheet
				open={sheet === 'mark'}
				onOpenChange={(open) => !open && setSheet(null)}
				title={`Mark ${count} conversation${count === 1 ? '' : 's'}`}
				items={[
					{ id: 'read', label: 'Mark as read', icon: MailCheck, onSelect: run(bulk.onMarkRead) },
					{ id: 'unread', label: 'Mark as unread', icon: MailWarning, onSelect: run(bulk.onMarkUnread) },
					{
						id: 'close',
						label: 'Close',
						icon: CircleCheck,
						disabled: !bulk.shouldShowOpenOrClosed,
						onSelect: run(bulk.onMarkClosed),
					},
					{
						id: 'open',
						label: 'Reopen',
						icon: FolderOpen,
						disabled: bulk.shouldShowOpenOrClosed,
						onSelect: run(bulk.onMarkOpen),
					},
				]}
			/>

			<ActionSheet
				open={sheet === 'assign'}
				onOpenChange={(open) => !open && setSheet(null)}
				title="Assign to"
				sections={[
					{
						id: 'none',
						items: [
							{ id: 'unassigned', label: 'Unassigned', onSelect: run(() => bulk.onAddAssigneesToConversations([])) },
						],
					},
					userOptions.length > 0 && {
						id: 'users',
						title: 'Users',
						items: userOptions.map((option) => ({
							id: option.value,
							label: option.label,
							onSelect: run(() => bulk.onAddAssigneesToConversations([option.value])),
						})),
					},
					departmentOptions.length > 0 && {
						id: 'departments',
						title: 'Departments',
						items: departmentOptions.map((option) => ({
							id: option.value,
							label: option.label,
							onSelect: run(() => bulk.onAddAssigneesToConversations([option.value])),
						})),
					},
				].filter(Boolean)}
			/>

			<ActionSheet
				open={sheet === 'move'}
				onOpenChange={(open) => !open && setSheet(null)}
				title="Move to folder"
				items={
					bulk.folders.length
						? bulk.folders.map((folder) => ({
								id: folder._id,
								label: folder.title || 'Untitled folder',
								onSelect: run(() => bulk.onMoveConversationsToFolder(folder._id)),
							}))
						: [{ id: 'none', label: 'No folders yet', disabled: true }]
				}
			/>

			<BulkTagSheet
				open={sheet === 'tag'}
				onOpenChange={(open) => !open && setSheet(null)}
				tags={bulk.tags || []}
				count={count}
				onApply={(tags) => run(() => bulk.onAddTagToConversations(undefined, tags))()}
			/>
		</>
	);
}

function BulkTagSheet({ open, onOpenChange, tags, count, onApply }) {
	const [query, setQuery] = useState('');
	const [picked, setPicked] = useState([]);

	useEffect(() => {
		if (open) {
			setQuery('');
			setPicked([]);
		}
	}, [open]);

	const trimmed = query.trim();
	const allTags = useMemo(() => [...new Set([...tags, ...picked])], [tags, picked]);
	const visible = trimmed
		? allTags.filter((tag) => tag.toLowerCase().includes(trimmed.toLowerCase()))
		: allTags;
	const canCreate = trimmed && !allTags.some((tag) => tag.toLowerCase() === trimmed.toLowerCase());

	const toggle = (tag) =>
		setPicked((current) =>
			current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag],
		);

	return (
		<BottomSheet
			open={open}
			onOpenChange={onOpenChange}
			size="large"
			title={`Tag ${count} conversation${count === 1 ? '' : 's'}`}
			headerLeft={<BottomSheetTextButton onClick={() => onOpenChange(false)}>Cancel</BottomSheetTextButton>}
			headerRight={
				<BottomSheetTextButton strong disabled={!picked.length} onClick={() => onApply(picked)}>
					Add
				</BottomSheetTextButton>
			}
		>
			<label className="mb-3 flex h-10 items-center gap-2 rounded-[10px] bg-zinc-200/70 px-3 dark:bg-zinc-800">
				<Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
				<input
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && canCreate) {
							e.preventDefault();
							toggle(trimmed);
							setQuery('');
						}
					}}
					placeholder="Search or create a tag"
					aria-label="Search or create a tag"
					className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
					autoCapitalize="none"
					autoCorrect="off"
					autoComplete="off"
					enterKeyHint="done"
				/>
			</label>
			<div role="group" aria-label="Tags" className="overflow-hidden rounded-xl bg-muted/40 dark:bg-zinc-800/60">
				{canCreate && (
					<button
						type="button"
						onClick={() => {
							toggle(trimmed);
							setQuery('');
						}}
						className="flex min-h-12 w-full items-center gap-3 px-4 text-left text-[17px] text-primary active:bg-muted"
					>
						<Plus className="size-5 shrink-0" aria-hidden="true" />
						<span className="truncate">Create “{trimmed}”</span>
					</button>
				)}
				{visible.map((tag, index) => {
					const checked = picked.includes(tag);
					return (
						<button
							key={tag}
							type="button"
							aria-pressed={checked}
							onClick={() => toggle(tag)}
							className={cn(
								'relative flex min-h-12 w-full items-center gap-3 px-4 text-left text-[17px] active:bg-muted',
							)}
						>
							{(index > 0 || canCreate) && (
								<span aria-hidden="true" className="absolute inset-x-0 top-0 ml-4 h-px bg-border/70" />
							)}
							<span className="min-w-0 flex-1 truncate">{tag}</span>
							{checked && <Check className="size-5 shrink-0 text-primary" strokeWidth={2.5} aria-hidden="true" />}
						</button>
					);
				})}
				{!visible.length && !canCreate && (
					<p className="px-4 py-6 text-center text-[15px] text-muted-foreground">No tags yet</p>
				)}
			</div>
		</BottomSheet>
	);
}
